import {
  Environment,
  InsightsRestrictions,
  PermissionStatus,
} from '@unu/ui-enums';

const DefaultPermissionStatus = PermissionStatus.Uninitialized;

const globalConfig = {
  context: {
    environment: Environment.Production,
  },
  plugins: {},
};

const debugError = (...args) => {
  globalConfig.context?.environment !== Environment.Production && console.error('Insights event sink error: ', ...args);
};

export const sendEvent = ({
  operation,
  args,
  plugins = globalConfig.plugins,
} = {}) => {

  const pluginsArray = Object.entries(plugins || {});

  if (!pluginsArray.length) {
    throw Error('No plugins specified. You need to registerPlugins.');
  }

  if (!operation) {
    return;
  }

  for (const [name, plugin] of pluginsArray) {

    if (!plugin?.permissionStatus || plugin.permissionStatus === PermissionStatus.Rejected) {
      continue;
    }

    if (plugin?.initializing || plugin.permissionStatus === PermissionStatus.Uninitialized) {

      plugin.queue.push(({
        operation,
        args,
      }));

      continue;
    }

    if (typeof plugin?.instance?.[operation] !== 'function') {
      continue;
    }

    try {

      plugin.instance[operation]({
        context: globalConfig.context,
        args,
      });
    } catch (error) {
      debugError(`Error calling ${operation} for ${name} plugin:`, error);
    }
  }
};

const flushQueue = ({
  queue = [],
  plugin = {},
} = {}) => {

  if (!Array.isArray(queue)) {
    return [];
  }

  for (const event of queue) {

    sendEvent({
      ...event,
      plugins: {
        approvedPlugin: plugin,
      },
    });
  }

  return [];
};

const getPluginPermissionStatus = plugin => {

  if (Array.isArray(plugin?.restrictions?.[InsightsRestrictions.Environments]) && !plugin.restrictions[InsightsRestrictions.Environments].includes(globalConfig.context?.environment)) {
    return PermissionStatus.Rejected;
  }

  return (!!plugin.permissionStatus && plugin.permissionStatus) || DefaultPermissionStatus;
};

const instantiatePlugin = ({
  name,
  plugin,
  queue,
} = {}) => {

  Promise.resolve(plugin.factory({
    ...plugin.initializationParams,
    context: globalConfig.context,
    debugError,
  }))
    .then(instance => {

      plugin.initializing = false;
      plugin.instance = instance;

      queue = flushQueue({
        queue,
        plugin,
      });
    })
    .catch(error => {
      debugError(`Error initializing plugin ${name}:`, error);
      plugin.instance = undefined;
    })
    .finally(() => {
      globalConfig.plugins[name] = {
        ...globalConfig.plugins[name],
        initializing: false,
        instance: plugin.instance,
        queue,
      };
    });
};

const initializePlugin = ({
  name,
  plugin,
} = {}) => {

  if (typeof plugin.factory !== 'function') {
    debugError(`${name} plugin did not provide a valid factory function`);
    return;
  }

  for (const [restrictionType, restriction] of Object.entries(plugin.overrides || {})) {
    plugin[restrictionType] = restriction;
  }

  const permissionStatus = getPluginPermissionStatus(plugin);
  const queue = (permissionStatus !== PermissionStatus.Rejected && Array.isArray(globalConfig.plugins?.[name]?.queue) && globalConfig.plugins[name].queue) || [];

  const updatedPlugin = {
    ...plugin,
    permissionStatus,
    queue,
  };

  if (permissionStatus === PermissionStatus.Approved) {

    globalConfig.plugins[name] = {
      ...updatedPlugin,
      initializing: !globalConfig.plugins[name]?.instance,
    };

    instantiatePlugin({
      name,
      plugin,
      queue,
    });

    return;
  }

  globalConfig.plugins[name] = updatedPlugin;
};

export const setContext = (
  context = {},
) => {

  if (typeof context !== 'object') {
    debugError('Context must be a valid object');
    return;
  }

  globalConfig.context = {
    ...globalConfig.context,
    ...context,
  };

  for (const [name, plugin] of Object.entries(globalConfig.plugins)) {

    if (typeof plugin?.instance?.setContext === 'function') {
      plugin.instance.setContext(globalConfig.context);
    }

    initializePlugin({
      name,
      plugin,
    });
  }
};

export const registerPlugins = (
  plugins = {},
) => {

  const pluginEntries = Object.entries(plugins || {});

  if (!pluginEntries.length) {
    debugError('Plugins must be a valid object');
    return;
  }

  for (const [name, plugin] of pluginEntries) {

    if (!name || typeof plugin !== 'object') {
      debugError(`Invalid plugin: ${name}`);
      continue;
    }

    initializePlugin({
      name,
      plugin,
    });
  }
};

const setGlobalPermissionStatus = permissionStatus => {

  if (!Object.values(PermissionStatus).includes(permissionStatus)) {
    debugError(`Attempted to set invalid global insights plugin permission status: ${permissionStatus}`);
    return;
  }

  for (const plugin of Object.values(globalConfig.plugins)) {

    plugin.overrides =  {
      ...plugin.overrides,
      permissionStatus,
    };
  }

  registerPlugins(globalConfig.plugins);
};

export const setPermissionStatus = pluginStatuses => {

  if (typeof pluginStatuses === 'string') {
    setGlobalPermissionStatus(pluginStatuses);
    return;
  }

  const statuses = Object.entries(pluginStatuses);

  if (!Array.isArray(statuses)) {
    debugError('When setting plugin permission statuses you must provide either a single status string or a valid object mapping from plugin names to permission status.');
    return;
  }

  for (const [name, status] of statuses) {

    if (!globalConfig.plugins?.[name]) {
      debugError(`Attempting to set permission for plugin ${name} which has not been registered.`);
      continue;
    }

    globalConfig.plugins[name] = {
      ...globalConfig.plugins[name],
      overrides: {
        ...globalConfig.plugins[name]?.overrides,
        permissionStatus: status,
      },
    };
  }

  registerPlugins(globalConfig.plugins);
};